import {
	createThemeIframeHelper,
	getLTIRef,
	saveAssessmentUrl,
	saveMobileAssessmentUrl,
	createModalIframe,
	AssessmentQueryInfo,
	initializeInstructionsIframe,
	observeAndRemoveExtensionIframes,
	listenForLTILoad,
	getInstituteMode,
} from "./helpers";

// Script to inject using Canvas themes for YuJa Verity
export {};
import { VerityOptions, AssessmentInfo, Lms } from "@verity/api-client/types";
import { isMobileDevice } from "../../helpers/device";
import checkStartAssessment from "./check-start-assessment";
import { checkReturnToAssessmentStart } from "./check-return-to-assessment-start";
import { LockdownLaunchInfo } from "../../portal/start-assessment";

declare global {
	interface Window {
		hasVerityTheme: boolean;
		verityInstituteId?: number;
		proctor?: boolean;
		ENV: {
			current_user_id: string;
			current_user_roles: string[];
			current_user_is_student: boolean;
		};
	}
}

// #region constants
const YUJA_VERITY_SUFFIX = "(YuJa Verity for Test Proctoring)";

const CANVAS = {
	ID: {
		CONTENT: "content",
	},
	CLASS: {
		TO_DO_SIDEBAR_ITEM: "ToDoSidebarItem",
	},
	SELECTOR: {
		TO_DO_SIDEBAR_LINK: `.ToDoSidebarItem > a`,
	},
};

const HOME = {
	URL_REGEX: /\/courses\/([0-9]+)\/?$/,
	ID: {
		DASHBOARD: "right-side",
	},
};

const MODULE = {
	URL_REGEX: /\/courses\/([0-9]+)\/modules\/?$/,
	CLASS: {
		QUIZ_LIST: "also_assignment",
		TO_DO_SIDEBAR_ITEM: CANVAS.CLASS.TO_DO_SIDEBAR_ITEM,
	},
	SELECTOR: {
		TO_DO_SIDEBAR_LINK: CANVAS.SELECTOR.TO_DO_SIDEBAR_LINK,
	},
};

const SYLLABUS = {
	URL_REGEX: /\/courses\/([0-9]+)\/assignments\/syllabus\/?$/,
	CLASS: {
		QUIZ_LIST: "name",
		TO_DO_SIDEBAR_ITEM: CANVAS.CLASS.TO_DO_SIDEBAR_ITEM,
	},
	SELECTOR: {
		TO_DO_SIDEBAR_LINK: CANVAS.SELECTOR.TO_DO_SIDEBAR_LINK,
	},
};

const ASSIGNMENT = {
	URL_REGEX: /\/courses\/([0-9]+)\/assignments\/?$/,
};

const GRADES = {
	URL_REGEX: /\/courses\/([0-9]+)\/grades\/?$/,
};

const QUIZ = {
	LIST_URL_REGEX: /\/courses\/([0-9]+)\/quizzes\/?$/,
	ID: {
		WRAPPER_DIV: "not_right_side",
	},
};

const OLD_QUIZ = {
	PREVIEW_URL_REGEX: /\/courses\/([0-9]+)\/quizzes\/([0-9]+)/,
	ID: {
		RIGHT_SIDE: "right-side",
		DISPLAY: "quiz_show", //
	},
};

const NEW_QUIZ = {
	PREVIEW_URL_REGEX: /\/courses\/([0-9]+)\/assignments\/([0-9]+)/,
	ID: {
		DISPLAY: "tool_content",
	},
};

const QUIZ_EDIT = {
	PREVIEW_URL_REGEX: /\/courses\/([0-9]+)\/quizzes\/([0-9]+)\/edit/,
	ID: {
		DISPLAY: "quiz_edit_wrapper",
	},
};

const QUIZ_TAKE = {
	PREVIEW_URL_REGEX: /\/courses\/([0-9]+)\/quizzes\/([0-9]+)\/take/,
	ID: {
		DISPLAY: "tool_content",
	},
};
// #endregion

// #region helper functions
function isProctoredQuizFromSuffix(element: HTMLElement | null): boolean {
	if (!element) {
		return false;
	}
	return element.textContent?.includes(YUJA_VERITY_SUFFIX) ?? false;
}

const checkIsInstructor = () => {
	return !(window.ENV.current_user_is_student || window.ENV.current_user_roles.includes("fake_student"));
};

function modifyCanvasQuizEntry(
	quizInfo: AssessmentInfo,
	quizOption: VerityOptions,
	assessmentUrl: string,
	isMobile?: boolean,
	iframeHelper?: HTMLIFrameElement,
) {
	const lockdownInfo: LockdownLaunchInfo = {
		target: assessmentUrl,
		assessmentIdInfo:
			quizInfo.courseId && quizInfo.quizId
				? {
						...quizInfo,
						lmsProvidedCourseId: quizInfo.courseId,
						lmsProvidedAssessmentId: quizInfo.quizId,
					}
				: undefined,
		thirdPartyOptions: quizOption.thirdPartyAssessmentOptions,
	};
	const startQuizAnchors = document.querySelectorAll<HTMLAnchorElement>(
		`a[href*="courses/${quizInfo.courseId}/quizzes/${quizInfo.quizId}/take"`,
	);
	if (startQuizAnchors.length === 0 && isMobile && iframeHelper && window.verityInstituteId !== undefined) {
		saveMobileAssessmentUrl(iframeHelper, window.location.href)
			.then(() => getLTIRef(iframeHelper, window.verityInstituteId!))
			.then((ltiRef) => {
				const baseUrl = window.location.href.split("/courses")[0];
				const courseId = window.location.pathname.split("/")[2];
				const authLink = `${baseUrl}/courses/${courseId}/external_tools/${ltiRef}`;
				window.top?.location.replace(authLink);
			})
			.catch(console.error);
		return;
	}
	for (const anchor of startQuizAnchors) {
		anchor.removeAttribute("disabled");
		anchor.setAttribute("style", "display:none");
		const newAnchor = document.createElement("a");
		newAnchor.classList.add(...anchor.classList);
		newAnchor.textContent = anchor.textContent;
		newAnchor.id = "start-yuja-test-proctoring";
		newAnchor.href = "#yuja";
		newAnchor.addEventListener("click", async (ev) => {
			ev.preventDefault();
			ev.stopPropagation();
			ev.stopImmediatePropagation();
			if (isMobile) {
				if (iframeHelper && window.verityInstituteId) {
					await saveMobileAssessmentUrl(iframeHelper, window.location.href);
					const ltiRef = await getLTIRef(iframeHelper, window.verityInstituteId);
					const authLink =
						window.location.href.split("/courses")[0] +
						"/courses/" +
						window.location.pathname.split("/")[2] +
						"/external_tools/" +
						ltiRef;
					window.location.href = authLink;
				}
			} else {
				// is desktop lockdown
				createModalIframe(lockdownInfo);
			}
		});

		// show only the new anchors
		anchor.parentNode?.insertBefore(newAnchor, anchor);
	}
}

// TODO: Can probably remove if better selectors are chosen
function getQuizListClassName(): string | null {
	// Home page can be customized to different page types
	if (HOME.URL_REGEX.test(window.location.pathname)) {
		const homePageType = document.getElementById("breadcrumbs")?.children[0].children[2].children[0];
		if ((homePageType as HTMLElement | null)?.textContent === "Syllabus") {
			return SYLLABUS.CLASS.QUIZ_LIST;
		} else if ((homePageType as HTMLElement | null)?.textContent === "Assignments") {
			return "assignment";
		} else {
			// Module
			return MODULE.CLASS.QUIZ_LIST;
		}
	}

	if (MODULE.URL_REGEX.test(window.location.pathname)) {
		return MODULE.CLASS.QUIZ_LIST;
	} else if (SYLLABUS.URL_REGEX.test(window.location.pathname)) {
		// must check before regAssignment
		return SYLLABUS.CLASS.QUIZ_LIST;
	} else if (ASSIGNMENT.URL_REGEX.test(window.location.pathname)) {
		return "assignment";
	} else if (GRADES.URL_REGEX.test(window.location.pathname)) {
		return "student_assignment editable";
	} else if (QUIZ.LIST_URL_REGEX.test(window.location.pathname)) {
		return "quiz";
	}
	return null;
}

async function observeAndHidePreviewMenuItem(quizListClassName: string, extensionConnectIframe: HTMLIFrameElement) {
	const instituteId = window.verityInstituteId;
	if (instituteId && (await getInstituteMode(extensionConnectIframe, instituteId))?.liteMode) {
		const ltiRefPromise = getLTIRef(extensionConnectIframe, instituteId);
		const hidePreviewMenuItem = () =>
			ltiRefPromise.then((ltiRef) => {
				if (ltiRef) {
					const resultsMenuEntry = document.querySelector(
						`.${quizListClassName} [role=menu] .menu_tool_link[data-tool-id="${ltiRef}"]`,
					);
					if (resultsMenuEntry) {
						resultsMenuEntry.parentElement?.remove();
					}
				}
			});
		new MutationObserver(hidePreviewMenuItem).observe(document, { childList: true, subtree: true });
		hidePreviewMenuItem();
	}
}

// #endregion

// #region Quiz List Page
// Disables a single quiz
// TODO: Move selectors to const
// TODO: Better selectors

function getAssessmentInfoFromUrl(url: string) {
	const match = /https:\/\/.*\/courses\/([0-9]+)\/(assignments|quizzes)\/([0-9]+)/.exec(url);
	return {
		lms: "canvas",
		lmsProvidedCourseId: match?.[1],
		lmsProvidedAssessmentId: match?.[3],
		hostName: new URL(url).hostname,
		isAssignments: match?.[2] === "assignments",
	};
}

type CanvasAssessmentInfo = ReturnType<typeof getAssessmentInfoFromUrl>;

async function correctAssessmentInfo({
	lmsProvidedCourseId,
	lmsProvidedAssessmentId,
	isAssignments,
}: CanvasAssessmentInfo): Promise<AssessmentQueryInfo> {
	// some assignments links have assignment ids, but are classic quizzes that were saved with classic quiz ids
	if (isAssignments) {
		try {
			const response = await fetch(`/api/v1/courses/${lmsProvidedCourseId}/assignments/${lmsProvidedAssessmentId}`);
			const assignment = response.ok ? await response.json() : undefined;
			if (typeof assignment === "object" && "quiz_id" in assignment && assignment.quiz_id) {
				isAssignments = false;
				lmsProvidedAssessmentId = assignment.quiz_id;
			}
		} catch (e) {
			console.error(e);
		}
	}
	return {
		instituteId: window.verityInstituteId,
		lmsProvidedCourseId,
		lmsProvidedAssessmentId,
		assessmentType: isAssignments ? "canvas-new-quiz" : "canvas-quiz",
	};
}

//#endregion

//#region Quiz Preview Page

function isQuizPreviewPage() {
	//FIXME: A few times I got the pathanme as something in the format of "/taking/723734". I am struggling to recreate this bug
	return (
		OLD_QUIZ.PREVIEW_URL_REGEX.test(window.location.pathname) ||
		NEW_QUIZ.PREVIEW_URL_REGEX.test(window.location.pathname)
	);
}

function isAssessmentVerityEnabledFromDom() {
	const titleTags = document.getElementsByTagName("title");
	if (titleTags) {
		for (const title of titleTags) {
			if (isProctoredQuizFromSuffix(title)) {
				return true;
			}
		}
		return false;
	}
	return null;
}

type BlockPage = "browser" | "download" | "update" | "mobile";

const createInstructionIframe = async (iframeHelper: HTMLIFrameElement, blockPage: BlockPage) => {
	const pageWrapperDiv = document.getElementById(QUIZ.ID.WRAPPER_DIV);
	const noticeWrapperDiv = pageWrapperDiv?.parentElement;
	if (!pageWrapperDiv || !noticeWrapperDiv) {
		return;
	}
	pageWrapperDiv.remove();

	const notice = document.createElement("iframe");
	notice.id = "yuja-download-instructions-theme";
	notice.style.overflow = "auto";
	notice.src = import.meta.env.VITE_VERITY_ORIGIN + "/warning/" + blockPage + ".html?lms=canvas";

	const iframeWrapper = document.getElementsByClassName("ic-Layout-contentMain")[0] as HTMLElement;
	if (iframeWrapper) {
		iframeWrapper.style.display = "flex";
		iframeWrapper.style.justifyContent = "center";
	}
	notice.style.border = "none";
	if (isMobileDevice()) {
		notice.height = "800px";
		notice.width = "100%";
	} else {
		notice.height = "700px";
		notice.width = "100%";
		const rightSideWrapper = document.getElementById("right-side-wrapper");
		if (rightSideWrapper) {
			rightSideWrapper.remove();
		}
	}
	initializeInstructionsIframe(notice, async () => {
		if (window.verityInstituteId) {
			const ltiRef = await getLTIRef(iframeHelper, window.verityInstituteId);
			const authLink =
				window.location.href.split("/courses")[0] +
				"/courses/" +
				window.location.pathname.split("/")[2] +
				"/external_tools/" +
				ltiRef;
			return {
				event: "init-auth-instructions",
				authLink: ltiRef ? authLink : undefined,
				quizLink: undefined,
			};
		}
	});

	noticeWrapperDiv.appendChild(notice);

	window.addEventListener("message", (ev) => {
		if (ev.origin !== import.meta.env.VITE_VERITY_ORIGIN) {
			return;
		}

		const message = ev.data;
		if (message?.event === "instructions-resize") {
			if (message.scrollHeight) {
				notice.height = message.scrollHeight + 100;
			}
			if (message.scrollWidth) {
				notice.width = "100%";
			}
			if (!message.noScroll) {
				document.documentElement.scrollTop = 0;
			}
		}
	});
};

function hideQuizPreviewPage() {
	const quizWrapperDiv = document.getElementById(QUIZ.ID.WRAPPER_DIV);
	if (quizWrapperDiv) {
		quizWrapperDiv.style.display = "none";
	}
}

function showQuizPreviewPage() {
	const quizWrapperDiv = document.getElementById(QUIZ.ID.WRAPPER_DIV);
	if (quizWrapperDiv) {
		quizWrapperDiv.style.removeProperty("display");
	}
}

//#endregion

//#region Quiz edit Page
function isQuizEditPage() {
	return QUIZ_EDIT.PREVIEW_URL_REGEX.test(window.location.pathname);
}

//#endregion

//#region Quiz take Page
function isQuizTakePage() {
	return QUIZ_TAKE.PREVIEW_URL_REGEX.test(window.location.pathname);
}

//#endregion

//#region extension

function sendInstituteId(iframe: HTMLIFrameElement): Promise<boolean> {
	const targetOrigin = import.meta.env.VITE_VERITY_ORIGIN;
	return new Promise((resolve, reject) => {
		if (iframe.contentWindow && window.verityInstituteId) {
			if (window.verityInstituteId) {
				iframe.contentWindow.postMessage(
					{
						type: "saveInstituteId",
						instituteId: window.verityInstituteId,
					},
					targetOrigin,
				);
				resolve(true);
			} else {
				resolve(false);
			}
		} else {
			reject();
		}
	});
}

async function start() {
	try {
		const assessmentInfo = getAssessmentInfoFromUrl(window.location.href);
		const reformattedAssessmentInfo = {
			lms: assessmentInfo.lms as Lms,
			hostname: assessmentInfo.hostName,
			courseId: assessmentInfo.lmsProvidedCourseId,
			quizId: assessmentInfo.lmsProvidedAssessmentId ?? "",
		};
		const iframeHelper = await createThemeIframeHelper();
		listenForLTILoad("canvas");
		void sendInstituteId(iframeHelper);
		const isCanvasInstructor =
			checkIsInstructor() ||
			document.getElementById("preview_quiz_button") ||
			window.top?.location.href.includes("speed_grader");
		const isAuthenticatedInstructor =
			!!isCanvasInstructor && document.getElementById("yuja-authentication-instructions") == null;

		const quizListClassName = getQuizListClassName();
		if (quizListClassName) {
			observeAndHidePreviewMenuItem(quizListClassName, iframeHelper);
		} else if (isQuizEditPage()) {
			// checkExtensionVersion(iframeHelper)
			// 	.then((val) => {
			// 		if (!val.exists) {
			// 			quizEditaddBanner();
			// 		}
			// 	})
			// 	.catch(() => {
			// 		quizEditaddBanner();
			// 	});
		} else if (isQuizTakePage() && !isAuthenticatedInstructor && window.location.href.includes("/take")) {
			const assessmentInfo = getAssessmentInfoFromUrl(window.location.href);
			if (
				await checkReturnToAssessmentStart({
					assessmentInfo: await correctAssessmentInfo(assessmentInfo),
					extensionConnectIframe: iframeHelper,
					checkVerityEnabledFallback: () => !!isAssessmentVerityEnabledFromDom(),
				})
			) {
				window.location.href = window.location.href.split("/take")[0]; // redirect to start (preview) page if the student doesn't have the extension
			}
		} else if (isQuizPreviewPage()) {
			const handleQuizPreviewPage = async () => {
				const assessmentInfo = getAssessmentInfoFromUrl(window.location.href);
				checkStartAssessment({
					getAssessmentInfo: () => correctAssessmentInfo(assessmentInfo),
					isInstructor: isAuthenticatedInstructor,
					extensionConnectIframe: iframeHelper,
					checkVerityEnabledFallback: () => !!isAssessmentVerityEnabledFromDom(),
					injectLoading: hideQuizPreviewPage,
					removeLoading: showQuizPreviewPage,
					injectUnsupportedBrowser: async () => {
						observeAndRemoveExtensionIframes();
						await createInstructionIframe(iframeHelper, "browser");
					},
					injectInstallExtension: async () => {
						observeAndRemoveExtensionIframes();
						await createInstructionIframe(iframeHelper, "download");
						void saveAssessmentUrl(iframeHelper, window.location.href, reformattedAssessmentInfo);
					},
					injectUpdateExtension: async () => {
						observeAndRemoveExtensionIframes();
						await createInstructionIframe(iframeHelper, "update");
						void saveAssessmentUrl(iframeHelper, window.location.href, reformattedAssessmentInfo);
					},
					injectLaunchLockdown: (assessmentProctoringOption) => {
						if (assessmentProctoringOption) {
							modifyCanvasQuizEntry(reformattedAssessmentInfo, assessmentProctoringOption, window.location.href);
						}
					},
					injectLaunchMobile: (assessmentProctoringOption) => {
						if (assessmentProctoringOption) {
							modifyCanvasQuizEntry(
								reformattedAssessmentInfo,
								assessmentProctoringOption,
								window.location.href,
								true,
								iframeHelper,
							);
						}
					},
				});
			};
			// TODO: multiple document.getElement calls may be reducible
			if (isAssessmentVerityEnabledFromDom() === null) {
				document.addEventListener("DOMContentLoaded", handleQuizPreviewPage);
			} else {
				await handleQuizPreviewPage();
			}
		}
	} catch (err) {
		console.error("YuJa Verity encountered an error: ", err);
	}

	window.addEventListener("message", (ev: MessageEvent<unknown>) => {
		if (ev.origin !== import.meta.env.VITE_VERITY_ORIGIN) {
			return;
		}
		if (typeof ev.data !== "object" || !ev.data) {
			return;
		}
		if ("type" in ev.data && ev.data.type === "getInstituteId") {
			ev.source?.postMessage(
				{
					type: "sendInstituteId",
					instituteId: window.verityInstituteId,
				},
				{
					targetOrigin: import.meta.env.VITE_VERITY_ORIGIN,
				},
			);
		}
	});
}

if (!window.hasVerityTheme) {
	window.hasVerityTheme = true;
	start().catch(console.error);
}
